import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { DeliveryNoteOverviewModalComponent } from '../../shared/modals/delivery-note-overview-modal/delivery-note-overview-modal.component';
import { OrderModalComponent } from '../../shared/modals/order-modal/order-modal.component';
import { DeliveryStatusEnum, IDeliveryNote as IFullDeliveryNote } from '../../shared/models/delivery-note.model';
import { OrderModalData } from '../../shared/models/modal-data.model';
import { IOrder } from '../../shared/models/order.model';
import { DeliveryNoteService } from '../../shared/services/delivery-note.service';
import { HelperService } from '../../shared/services/helper.service';
import { ModalService } from '../../shared/services/modal.service';
import { OrderService } from '../../shared/services/order.service';
import { ToastService } from '../../shared/services/toast.service';


@Component({
  selector: 'app-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, DeliveryNoteOverviewModalComponent],
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
})
export class OrdersComponent implements OnInit, OnDestroy {
  public orders: IOrder[] = [];
  public isLoading = false;

  public expandedOrderIds: Set<string> = new Set();
  public orderDeliveryNotes: Map<string, IFullDeliveryNote[]> = new Map();
  public loadingDeliveryNotes: Set<string> = new Set();

  public showOverviewModal = false;
  public selectedDeliveryNote: IFullDeliveryNote | null = null;
  public selectedOrderInfo = '';

  public searchTerm = '';
  public searchTerms = new Subject<string>();

  public currentPage = 1;
  public pageSize = 10;
  public totalItems = 0;
  public totalPages = 1;

  public sortField = 'createdAt';
  public sortDirection = 'desc';

  private subscriptions = new Subscription();

  constructor(
    private orderService: OrderService,
    private modalService: ModalService,
    private deliveryNoteService: DeliveryNoteService,
    private toastService: ToastService,
    public helperService: HelperService
  ) { }

  ngOnInit(): void {
    this.loadOrders();
    this.subscriptions.add(
      this.searchTerms.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
        this.currentPage = 1;
        this.onSearch();
      })
    );
  }

  public loadOrders(): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.orderService.getOrders(this.currentPage, this.pageSize, this.searchTerm, this.sortField, this.sortDirection).subscribe({
        next: response => {
          if (response.DATA) {
            this.orders = response.DATA.data;
            this.totalItems = response.DATA.total;
            this.totalPages = response.DATA.totalPages;
          }
          this.isLoading = false;
        },
        error: () => {
          console.error('Error loading orders');
          this.isLoading = false;
        },
      })
    );
  }

  public onSearch(): void {
    this.currentPage = 1;
    this.loadOrders();
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.loadOrders();
  }

  public onSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadOrders();
  }

  public getSortIcon(field: string): string {
    return this.helperService.getSortIcon(this.sortField, this.sortDirection, field);
  }

  public formatDate(date: Date | string | undefined): string {
    if (!date) return '—';
    const d = new Date(date);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  public getDeliveryNotesCount(order: IOrder): number {
    // Use deliveryNoteCount from backend (counts from separate collection)
    // Fall back to embedded deliveryNotes array length if not available
    return order.deliveryNoteCount ?? 0;
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.searchTerms.next('');
  }

  public openOrderModal(order?: IOrder): void {
    const isEdit = !!order;
    const data: OrderModalData = isEdit ? { mode: 'edit', order } : { mode: 'create' };

    this.modalService
      .open(OrderModalComponent, data)
      .then((result: IOrder) => {
        if (!result) return;

        const onSuccess = (): void => {
          const successMessage = isEdit ? 'Auftrag erfolgreich aktualisiert' : 'Auftrag erfolgreich erstellt';
          this.toastService.showSuccess(successMessage);
          this.loadOrders();
        };

        const onError = (): void => {
          const errorMessage = isEdit ? 'Fehler beim Aktualisieren des Auftrags' : 'Fehler beim Erstellen des Auftrags';
          this.toastService.showError(errorMessage);

        };

        if (isEdit && result._id) {
          this.orderService.updateOrder(result._id, result).subscribe({ next: onSuccess, error: onError });
        } else {
          this.orderService.createOrder(result).subscribe({ next: onSuccess, error: onError });
        }
      })
      .finally(() => {
        this.modalService.close();
      });
  }

  public onSearchChange(): void {
    this.searchTerms.next(this.searchTerm);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public toggleRow(order: IOrder): void {
    const orderId = order._id;
    if (!orderId) return;

    // Only allow expansion if there are delivery notes
    if (this.getDeliveryNotesCount(order) === 0) return;

    if (this.expandedOrderIds.has(orderId)) {
      this.expandedOrderIds.delete(orderId);
    } else {
      this.expandedOrderIds.add(orderId);
      // Load delivery notes if not already loaded
      if (!this.orderDeliveryNotes.has(orderId)) {
        this.loadDeliveryNotesForOrder(orderId);
      }
    }
  }

  public isRowExpanded(order: IOrder): boolean {
    return order._id ? this.expandedOrderIds.has(order._id) : false;
  }

  public loadDeliveryNotesForOrder(orderId: string): void {
    this.loadingDeliveryNotes.add(orderId);
    this.subscriptions.add(
      this.deliveryNoteService.getDeliveryNotesByOrderId(orderId).subscribe({
        next: response => {
          if (response.DATA) {
            this.orderDeliveryNotes.set(orderId, response.DATA);
          }
          this.loadingDeliveryNotes.delete(orderId);
        },
        error: () => {
          console.error('Error loading delivery notes for order', orderId);
          this.loadingDeliveryNotes.delete(orderId);
        },
      })
    );
  }

  public getDeliveryNotesForOrder(orderId: string): IFullDeliveryNote[] {
    return this.orderDeliveryNotes.get(orderId) || [];
  }

  public isLoadingDeliveryNotes(orderId: string): boolean {
    return this.loadingDeliveryNotes.has(orderId);
  }

  public openDeliveryNoteModal(deliveryNote: IFullDeliveryNote, order: IOrder): void {
    this.selectedDeliveryNote = deliveryNote;
    this.selectedOrderInfo = `${order.internalOrderId} - ${order.companyName}`;
    this.showOverviewModal = true;
  }

  public closeOverviewModal(): void {
    this.showOverviewModal = false;
    this.selectedDeliveryNote = null;
    this.selectedOrderInfo = '';
  }

  public onEditFromModal(deliveryNote: IFullDeliveryNote): void {
    this.closeOverviewModal();
    // Navigate to edit page or open edit modal
    // For now, we'll just close the modal
    console.log('Edit delivery note:', deliveryNote._id);
  }

  public getStatusName(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusName(status);
  }

  public getStatusClass(status: DeliveryStatusEnum): string {
    return this.deliveryNoteService.getStatusClass(status);
  }

  public getPositionsCount(deliveryNote: IFullDeliveryNote): number {
    return deliveryNote.positions?.length || 0;
  }
}
